/**
 * Swiper + Featured Collection — from Renovate Greenscape theme (class names unchanged)
 */
if (!customElements.get('swiper-section')) {
  class SwiperSection extends HTMLElement {
    connectedCallback() {
      this.swiperEl = this.querySelector('.swiper');
      if (this.swiperEl) {
        this.waitForSwiper().then(() => this.init());
      }
    }

    waitForSwiper() {
      return new Promise((resolve) => {
        const check = () => {
          if (window.Swiper) resolve();
          else requestAnimationFrame(check);
        };
        check();
      });
    }

    init() {
      if (this.swiper) return;

      this.slidesDesktop = Number(this.dataset.slidesDesktop) || 4;
      this.slidesTablet = Number(this.dataset.slidesTablet) || 3;
      this.slidesMobile = parseFloat(this.dataset.slidesMobile);
      if (Number.isNaN(this.slidesMobile) || this.slidesMobile <= 0) {
        this.slidesMobile = 1;
      }
      this.slidesCustom = Number(this.dataset.slidesCustom) || 2;
      this.speed = Number(this.dataset.speed) || 600;
      this.loop = this.dataset.loop === 'true';
      this.freeMode = this.dataset.freeMode === 'true';
      this.effect = this.dataset.effect || 'slide';
      this.gap = Number(this.dataset.gap || this.dataset.spacing) || 20;
      this.customGap = Number(this.dataset.customGap || this.dataset.gap || this.dataset.spacing) || 20;
      this.mobileGap = Number(this.dataset.mobileGap || this.dataset.spacing) || 20;
      this.centerMode = this.dataset.centerMode === 'true';
      this.observeParents = this.dataset.observeParents === 'true';
      this.autoHeight = this.dataset.autoHeight === 'true';
      this.grabCursor = this.dataset.grabCursor === 'true';
      this.watchSlidesProgress = this.dataset.progress === 'true';
      this.paginationEl = this.querySelector('.swiper-pagination');
      this.paginationType = this.dataset.paginationType || 'bullets';
      this.autoplayEnabled = this.dataset.autoplay === 'true' || this.dataset.autoplay === '1';
      this.delay = Number(this.dataset.delay) || 3000;

      this.createSwiper();
    }

    createSwiper() {
      if (this.swiper) return;

      this.swiperEl.classList.remove('fc-slider--preview');

      const options = {
        slidesPerView: this.slidesMobile,
        spaceBetween: this.gap,
        speed: this.speed,
        loop: this.loop,
        freeMode: this.freeMode,
        centeredSlides: this.centerMode,
        grabCursor: this.grabCursor,
        effect: this.effect,
        observeParents: this.observeParents,
        autoHeight: this.autoHeight,
        watchSlidesProgress: this.watchSlidesProgress,
        breakpoints: {
          0: {
            slidesPerView: this.slidesMobile,
            spaceBetween: this.mobileGap,
            freeMode: false,
          },
          640: {
            slidesPerView: this.slidesCustom,
            spaceBetween: this.customGap,
          },
          899: {
            slidesPerView: this.slidesTablet,
            spaceBetween: this.customGap,
          },
          1199: {
            slidesPerView: this.slidesDesktop,
            spaceBetween: this.gap,
          },
        },
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev'),
        },
      };

      if (this.autoplayEnabled) {
        options.autoplay = { delay: this.delay, disableOnInteraction: false };
      }

      if (this.paginationEl) {
        options.pagination = {
          el: this.paginationEl,
          clickable: true,
          type: this.paginationType,
        };
      }

      this.swiper = new Swiper(this.swiperEl, options);
      this.swiperEl.swiperInstance = this.swiper;
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  customElements.define('swiper-section', SwiperSection);
}

if (!customElements.get('featured-collection')) {
  class FeaturedCollection extends HTMLElement {
    constructor() {
      super();
      this.currentSlideNumber = 1;
      this.totalSlides = 1;
      this.navigableSlides = 1;
      this.view = this.dataset.view || 'grid';
      this.swiper = null;
      this.headingWrapper = null;
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      if (this.view !== 'slider') return;

      const container = this.parentElement?.parentElement;
      if (container) {
        this.headingWrapper = container.querySelector('.featured_collection_head');
      }

      if (!this.headingWrapper) return;

      this.waitForSwiper();
    }

    waitForSwiper() {
      const swiperSection = this.querySelector('swiper-section');
      if (!swiperSection) return;

      if (swiperSection.swiper) {
        this.swiper = swiperSection.swiper;
        this.initSwiper();
        return;
      }

      let checkCount = 0;
      const checkInterval = setInterval(() => {
        checkCount += 1;
        if (swiperSection.swiper) {
          clearInterval(checkInterval);
          this.swiper = swiperSection.swiper;
          this.initSwiper();
        } else if (checkCount > 50) {
          clearInterval(checkInterval);
        }
      }, 100);
    }

    initSwiper() {
      if (!this.swiper) return;

      const swiperEl = this.querySelector('.swiper');
      if (!swiperEl) return;

      const productCards = swiperEl.querySelectorAll('.product_card_wrapper:not(.swiper-slide-duplicate)');
      this.totalSlides = productCards.length > 0 ? productCards.length : this.swiper.slides.length;

      const slidesPerView = this.swiper.params.slidesPerView || 1;
      this.navigableSlides = Math.max(1, this.totalSlides - Math.ceil(slidesPerView) + 1);

      const totalSlideElement = this.headingWrapper.querySelector('.fc_total_slide');
      if (totalSlideElement) {
        totalSlideElement.textContent = String(this.navigableSlides).padStart(2, '0');
      }

      this.swiper.on('slideChange', () => {
        this.updateSlideNumber();
        this.syncKeyboardAccess();
      });
      this.swiper.on('transitionEnd', () => {
        this.syncKeyboardAccess();
      });

      window.addEventListener('resize', this.handleResize);
      this.updateSlideNumber();
      this.syncKeyboardAccess();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
    }

    updateSlideNumber() {
      if (!this.swiper || !this.headingWrapper) return;

      let index = this.swiper.realIndex;
      if (this.navigableSlides > 0) {
        index = index % this.navigableSlides;
      }
      this.currentSlideNumber = index + 1;
      this.updateDisplay();
    }

    updateDisplay() {
      const currentSlideElement = this.headingWrapper.querySelector('.fc_current_slide');
      const progressBar = this.headingWrapper.querySelector('.fc_pagination_progress_inner');

      if (currentSlideElement) {
        currentSlideElement.style.opacity = '0';
        currentSlideElement.style.transform = 'scale(0.8)';
        setTimeout(() => {
          currentSlideElement.textContent = String(this.currentSlideNumber).padStart(2, '0');
          currentSlideElement.style.opacity = '1';
          currentSlideElement.style.transform = 'scale(1)';
        }, 150);
      }

      if (progressBar && this.navigableSlides > 0) {
        const progress = (this.currentSlideNumber / this.navigableSlides) * 100;
        progressBar.style.width = `${progress}%`;
      }
    }

    handleResize() {
      this.syncKeyboardAccess();
    }

    syncKeyboardAccess() {
      if (!this.swiper) return;

      const slides = Array.from(this.querySelectorAll('.product_card_wrapper.swiper-slide'));
      const viewport = this.querySelector('.swiper');

      slides.forEach((slide) => {
        const isVisible = this.isSlideKeyboardVisible(slide, viewport);
        this.toggleFocusableElements(slide, isVisible);
      });

      this.syncArrowFocusability();
    }

    isSlideKeyboardVisible(slide, viewport) {
      if (!slide || !viewport || slide.classList.contains('swiper-slide-duplicate')) return false;

      const slideRect = slide.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      const overlap = Math.min(slideRect.right, viewportRect.right) - Math.max(slideRect.left, viewportRect.left);

      return overlap > Math.min(slideRect.width * 0.6, viewportRect.width);
    }

    toggleFocusableElements(container, enabled) {
      container.querySelectorAll('a[href], button, input, select, textarea, [tabindex]').forEach((element) => {
        if (enabled) {
          const originalTabindex = element.dataset.originalTabindex;
          if (originalTabindex !== undefined) {
            if (originalTabindex === '') element.removeAttribute('tabindex');
            else element.setAttribute('tabindex', originalTabindex);
          } else if (
            element.classList.contains('quick_add_btn') ||
            element.classList.contains('product_form_submit_btn')
          ) {
            element.removeAttribute('tabindex');
          }
        } else {
          if (element.dataset.originalTabindex === undefined) {
            element.dataset.originalTabindex = element.getAttribute('tabindex') ?? '';
          }
          element.setAttribute('tabindex', '-1');
        }
      });
    }

    syncArrowFocusability() {
      this.querySelectorAll('.fc_btn').forEach((arrow) => {
        if (arrow.classList.contains('swiper-button-disabled') || arrow.disabled) {
          arrow.setAttribute('tabindex', '-1');
        } else {
          arrow.removeAttribute('tabindex');
        }
      });
    }
  }

  customElements.define('featured-collection', FeaturedCollection);
}

if (!customElements.get('fc-product-form')) {
  class FcProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.submitButton = this.form.querySelector('[type="submit"]');
      this.loader = this.submitButton?.querySelector('.loader');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(evt) {
      evt.preventDefault();

      if (!this.submitButton || this.submitButton.disabled || this.submitButton.hasAttribute('disabled')) {
        return;
      }

      this.submitButton.classList.add('loading');
      this.submitButton.setAttribute('aria-disabled', 'true');
      if (this.loader) this.loader.classList.remove('hidden');

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { headers: {} };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      config.body = formData;

      fetch(`${window.routes?.cart_add_url || '/cart/add'}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            const errorEl = this.closest('.product_card_wrapper')?.querySelector('.product_add_error');
            if (errorEl) {
              errorEl.textContent = response.description || response.message;
              errorEl.classList.remove('hidden');
            }
            return;
          }

          if (!this.cart) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'fc-product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }

          if (typeof this.cart.renderContents === 'function') {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => console.error(e))
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (!this.submitButton.hasAttribute('disabled')) {
            this.submitButton.removeAttribute('aria-disabled');
          }
          if (this.loader) this.loader.classList.add('hidden');
        });
    }
  }

  customElements.define('fc-product-form', FcProductForm);
}
